import { X } from "lucide-react";
import type { GraphNode, GraphLink } from "./KnowledgeGraph";

interface NodeDetailPanelProps {
	node: GraphNode;
	links: GraphLink[];
	nodes: GraphNode[];
	onClose: () => void;
}

export default function NodeDetailPanel({ node, links, nodes, onClose }: NodeDetailPanelProps) {
	const connectedLinks = links.filter((l) => {
		const sid = typeof l.source === "object" ? (l.source as any).id : l.source;
		const tid = typeof l.target === "object" ? (l.target as any).id : l.target;
		return sid === node.id || tid === node.id;
	});

	return (
		<div className="absolute top-4 left-4 w-72 max-h-[50vh] bg-slate-800/90 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-4 overflow-y-auto z-30 shadow-2xl">
			<div className="flex items-center justify-between mb-3">
				<h3 className="text-white font-semibold text-sm uppercase tracking-wider">Node Detail</h3>
				<button onClick={onClose} className="p-1 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700 transition-all" title="Close">
					<X className="w-4 h-4" />
				</button>
			</div>
			<div className="space-y-3">
				<div>
					<span className="text-xs text-slate-500 uppercase tracking-wider">Name</span>
					<p className="text-white font-medium">{node.name}</p>
				</div>
				<div>
					<span className="text-xs text-slate-500 uppercase tracking-wider">ID</span>
					<p className="text-slate-400 text-sm font-mono break-all">{node.id}</p>
				</div>
				{node.properties && Object.keys(node.properties).length > 0 && (
					<div>
						<span className="text-xs text-slate-500 uppercase tracking-wider">Properties</span>
						<div className="mt-1 space-y-1">
							{Object.entries(node.properties).map(([key, value]) => (
								<div key={key} className="flex justify-between text-sm bg-slate-900/50 rounded-lg px-3 py-1.5">
									<span className="text-slate-400">{key}</span>
									<span className="text-white font-mono">{String(value)}</span>
								</div>
							))}
						</div>
					</div>
				)}
				<div>
					<span className="text-xs text-slate-500 uppercase tracking-wider">
						Relations ({connectedLinks.length})
					</span>
					<div className="mt-1 space-y-1">
						{connectedLinks.map((link) => {
							const sid = typeof link.source === "object" ? (link.source as any).id : link.source;
							const tid = typeof link.target === "object" ? (link.target as any).id : link.target;
							const isOutgoing = sid === node.id;
							const otherNodeId = isOutgoing ? tid : sid;
							const otherNode = nodes.find((n) => n.id === otherNodeId);
							return (
								<div key={link.rid} className="text-sm bg-slate-900/50 rounded-lg px-3 py-1.5">
									<span className="text-slate-400">{isOutgoing ? "→" : "←"} </span>
									<span className="text-blue-400">{link.name}</span>
									<span className="text-slate-400"> {isOutgoing ? "→" : "←"} </span>
									<span className="text-white">{otherNode?.name || otherNodeId}</span>
								</div>
							);
						})}
					</div>
				</div>
			</div>
		</div>
	);
}
